import React from 'react'
import { Campaign, CampaignStatus, Label } from '../__generated__/graphql'
import { TagsCell } from '../components/lists/Table/shared/TagsCell'
import { DateCell } from '../components/lists/Table/shared/DateCell'
import PacingCell from '../components/lists/Table/shared/PacingCell'
import PublicationStatusBadge from '../components/badges/PublicationStatus'
import { Tooltip } from 'antd'

const truncate = (text: string, maxLength: number = 25): string => {
  if (!text) return ''
  return text.length > maxLength
    ? text.substring(0, maxLength - 2) + '...'
    : text
}

const renderCostMetric = (divisorField: 'impressions' | 'tdClicks' | 'clicks' | 'reads' | 'conversions', multiplier = 1) => {
  return (_: any, record: any) => {
    const divisor = record[divisorField] || 0
    const cost = record.cost || 0
    const value = divisor > 0 ? (cost / divisor) * multiplier : 0
    return (
      <span className='text-sm'>
        {value > 0 ? `${value.toFixed(2)} DKK` : '-'}
      </span>
    )
  }
}

const getCampaignWarning = (campaign: Campaign, companyId: string) => {
  if (!ownCampaign(campaign, companyId)) {
    return ''
  }

  const publications = campaign.publications as any[]

  if (
    [
      CampaignStatus.Ready,
      CampaignStatus.Active,
      CampaignStatus.Paused,
    ].includes(campaign.status as CampaignStatus)
  ) {
    const publicationsThatRequireApproval = publications.filter((p) =>
      [
        'Draft',
        'ReviewingByPublisher',
      ].includes(p.status)
    ).length
    if (publicationsThatRequireApproval > 0) {
      return `${publicationsThatRequireApproval} out of ${publications.length} websites requires approval`
    }
  }

  return ''
}

const EnumCampaignStatusText = {
  [CampaignStatus.Active]: 'Active',
  [CampaignStatus.Archived]: 'Archived',
  [CampaignStatus.Draft]: 'Draft',
  [CampaignStatus.Finished]: 'Finished',
  [CampaignStatus.Paused]: 'Paused',
  [CampaignStatus.Ready]: 'Ready',
  [CampaignStatus.ReviewingByAdvertiser]: 'Reviewing by advertiser',
}

const ownCampaign = (campaign: Campaign, companyId: string) => {
  return campaign.ownerCompanyId === companyId
}

export const getCampaignsColumns = (
  company: {_id: string; allowedMarketplaces: string[]},
  filters = {status: true, owner: true},
  labels?: Label[],
  advertiserFilters?: {text: string; value: string}[],
  defaultFilters: Record<string, any> = {},
  navigate?: (path: string) => void
) => [
  {
    dataIndex: 'name',
    title: 'Campaign',
    key: 'name',
    render: (text: string, record: Campaign) => {
      const isOwner = record.ownerCompanyId === company._id
      const href = isOwner 
        ? `/campaigns/${record._id}/details`
        : `/campaigns/${record._id}/report`
      
      return (
        <Tooltip title={text} placement="top">
          <a 
            href={href}
            className='text-sm text-gray-900 hover:text-gray-900 no-underline'
            onClick={(e) => {
              // Stop propagation to prevent row click handler from firing
              e.stopPropagation()
              
              // If navigate function is provided and it's a left click, use it
              if (navigate && e.button === 0) {
                e.preventDefault()
                navigate(href)
              }
            }}
          >
            {truncate(text)}
          </a>
        </Tooltip>
      )
    },
  },
  {
    dataIndex: 'labels',
    title: 'Labels',
    key: 'labels',
    hidden: !labels,
    defaultFilteredValue: defaultFilters.labels,
    render: TagsCell,
    filters: labels?.map(({name, _id}) => ({text: name, value: _id})),
  },
  ...(company.allowedMarketplaces.length > 1
    ? [
        {
          dataIndex: 'marketplace',
          title: 'Marketplace',
          key: 'marketplace',
        },
      ]
    : []),
  {
    dataIndex: 'advertiser',
    title: 'Advertiser',
    key: 'advertiserId',
    defaultFilteredValue: defaultFilters.advertiserId,
    render: (text: string) => <span className='text-sm'>{truncate(text)}</span>,
    filters: advertiserFilters,
  },
  {
    dataIndex: 'status',
    title: 'Status',
    key: 'status',
    defaultFilteredValue: defaultFilters.status,
    filters: filters.status
      ? Object.values(CampaignStatus).map((status) => {
          return {
            text: EnumCampaignStatusText[status],
            value: status,
          }
        })
      : undefined,
    render: (text: any, record: Campaign) => (
      <PublicationStatusBadge
        status={text}
        warning={getCampaignWarning(record, company._id)}
      />
    ),
  },
  {
    dataIndex: 'pacing',
    title: 'Pacing',
    key: 'pacing',
    render: (_: any, record: any) => (
      <PacingCell record={record} hidden={!ownCampaign(record, company._id)} />
    ),
  },
  {
    dataIndex: 'budget',
    title: 'Budget',
    key: 'budget',
    hidden: !filters?.owner,
    render: (text: any, record: Campaign) => {
      if (!ownCampaign(record, company._id)) {
        return <span className='text-sm text-gray-400'>N/A</span>
      }

      const targetValue = record.targetValue ? Number(record.targetValue) : 0
      const dailyBudgetLimit = record.dailyBudgetLimit
        ? Number(record.dailyBudgetLimit)
        : 0
      const budgetItems: string[] = []

      if (record.targetType === 'BUDGET' && targetValue > 0) {
        budgetItems.push(`${targetValue.toLocaleString()} DKK`)
      }

      if (dailyBudgetLimit > 0) {
        budgetItems.push(`${dailyBudgetLimit.toLocaleString()} DKK/day`)
      }

      return budgetItems.length > 0 ? (
        <span className='text-sm'>{budgetItems.join(', ')}</span>
      ) : (
        <span className='text-sm'>-</span>
      )
    },
  },
  {
    dataIndex: 'cost',
    title: 'Current Spend',
    key: 'cost',
    hidden: !filters?.owner,
    render: (cost: number, record: Campaign) => {
      if (!ownCampaign(record, company._id)) {
        return <span className='text-sm text-gray-400'>N/A</span>
      }

      const costValue = cost || 0
      return <span className='text-sm'>{costValue.toLocaleString()} DKK</span>
    },
  },
  {
    title: 'Start Date',
    dataIndex: 'startDate',
    key: 'startDate',
    sorter: true,
    render: (text: string) => <DateCell value={text} />,
  },
  {
    title: 'End Date',
    dataIndex: 'endDate',
    key: 'endDate',
    sorter: true,
    render: (text: string) => <DateCell value={text} />,
  },
]

export const getMarketplaceColumns = (
  advertiserFilters?: {text: string; value: string}[],
  defaultFilters: Record<string, any> = {},
  navigate?: (path: string) => void
) => [
  {
    dataIndex: 'name',
    title: 'Campaign',
    key: 'name',
    render: (text: string, record: Campaign) => {
      const href = `/marketplace-campaigns/${record._id}/websites`

      return (
        <Tooltip title={text} placement="top">
          <a
            href={href}
            className='text-sm text-gray-900 hover:text-gray-900 no-underline'
            onClick={(e) => {
              e.stopPropagation()

              if (navigate && e.button === 0) {
                e.preventDefault()
                navigate(href)
              }
            }}
          >
            {truncate(text)}
          </a>
        </Tooltip>
      )
    },
  },
  {
    dataIndex: 'advertiser',
    title: 'Advertiser',
    key: 'advertiserId',
    defaultFilteredValue: defaultFilters.advertiserId,
    render: (text: string) => <span className='text-sm'>{truncate(text)}</span>,
    // filters: advertiserFilters,
  },
  {
    dataIndex: 'status',
    title: 'Status',
    key: 'status',
    render: (status: CampaignStatus) => <PublicationStatusBadge status={status} />,
  },
  {
    title: 'Start Date',
    dataIndex: 'startDate',
    key: 'startDate',
    sorter: true,
    render: (text: string) => <DateCell value={text} />,
  },
  {
    title: 'End Date',
    dataIndex: 'endDate',
    key: 'endDate',
    sorter: true,
    render: (text: string) => <DateCell value={text} />,
  },
  {
    dataIndex: 'impressions',
    title: 'Impressions',
    key: 'impressions',
    render: (value: number) => (
      <span className='text-sm'>{value ? value.toLocaleString() : '-'}</span>
    ),
  },
  {
    dataIndex: 'revenue',
    title: 'Revenue',
    key: 'revenue',
    render: (value: number) => (
      <span className='text-sm'>{value ? `${value.toLocaleString()} DKK` : '-'}</span>
    ),
  },
  {
    dataIndex: 'ecpm',
    title: 'eCPM',
    key: 'ecpm',
    render: renderCostMetric('impressions', 1000),
  },
  {
    dataIndex: 'ecpc',
    title: 'eCPC',
    key: 'ecpc',
    render: renderCostMetric('tdClicks'),
  },
  {
    dataIndex: 'ecpr',
    title: 'eCPR',
    key: 'ecpr',
    render: renderCostMetric('reads'),
  },
]
